import { Node_Type } from 'ricos-types';
import React, { useState, useContext } from 'react';
import { RicosContext, RicosViewerContext } from 'ricos-context';
import classNames from 'classnames';
import { isSSR, getLangDir } from 'wix-rich-content-common';
import PropTypes from 'prop-types';
import { getScaleImageSrc } from 'wix-rich-content-common/libs/imageUtils';
import Tooltip from 'wix-rich-content-common/libs/Tooltip';
import { isNumber } from 'lodash';

const VERTICAL_EMBED_TYPE = "wix-draft-plugin-vertical-embed";

var styles = {"card":"W7Zor","link":"NaCQ6","container":"Liikc","image":"_168g5","right":"vLeLK","cardLayout":"NiWwi","content":"diHVC","title":"_94hYj","subtitle":"ACvVt","button":"PpB-Z","button_disabled":"_1fN8y","buttonText":"_51PRa","slimLayout":"HjXVj","rtl":"GmKSf","ltr":"_3SVZs"};

const subtitle = (info) => {
  return info.leftSubtitle && /* @__PURE__ */ React.createElement("div", { className: styles.subtitle }, info.leftSubtitle, info.rightSubtitle && /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("span", { className: styles.right }, "|"), info.rightSubtitle));
};
const ellipsisTitle = (title) => {
  return title.length <= 80 ? title : `${title.substring(0, 80)}...`;
};
const CardContent = (props) => {
  const { title, info, buttonText, disabled, t } = props;
  return /* @__PURE__ */ React.createElement("div", { className: styles.content }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", { className: styles.title }, ellipsisTitle(title)), info && subtitle(info)), /* @__PURE__ */ React.createElement(Tooltip, { content: disabled ? t("VerticalEmbed_Disabled_Button_Tooltip") : null }, /* @__PURE__ */ React.createElement("div", { className: classNames(styles.button, { [styles.button_disabled]: disabled }) }, /* @__PURE__ */ React.createElement("div", { className: styles.buttonText }, buttonText))));
};
CardContent.propTypes = {
  title: PropTypes.string.isRequired,
  info: PropTypes.object,
  buttonText: PropTypes.string.isRequired,
  disabled: PropTypes.string.boolean,
  t: PropTypes.func.isRequired
};

var __defProp$2 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
const getImageTargetHeight = (width, height, targetWidth) => {
  const proportion = height / width;
  const targetHeight = proportion * targetWidth;
  return targetHeight;
};
const Card = (props) => {
  const { url, content, direction, imageWidth, imageHeight, t, disabled } = props;
  const [imageSrc, setImageSrc] = useState();
  const getImageSrc = (targetWidth) => {
    const targetHeight = getImageTargetHeight(imageWidth, imageHeight, targetWidth);
    return getScaleImageSrc(props.imageSrc, targetWidth, targetHeight);
  };
  const setRef = (ref) => {
    var _a;
    if (ref) {
      const targetWidth = (_a = ref.getBoundingClientRect()) == null ? void 0 : _a.width;
      if (imageWidth && imageHeight) {
        const imageSrc2 = getImageSrc(targetWidth);
        setImageSrc(imageSrc2);
      } else {
        setImageSrc(props.imageSrc);
      }
    }
  };
  const imageUrl = isSSR() ? imageWidth && imageHeight ? getImageSrc(185) : props.imageSrc : imageSrc;
  const imageStyle = imageUrl ? { backgroundImage: `url(${imageUrl})` } : {};
  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    /* @__PURE__ */ React.createElement("a", { className: styles.link, href: disabled ? null : url, target: "_blank", ref: setRef }, /* @__PURE__ */ React.createElement(
      "div",
      {
        style: { direction },
        className: classNames(styles[direction], styles.container, styles.cardLayout)
      },
      props.imageSrc && /* @__PURE__ */ React.createElement("div", { style: imageStyle, className: styles.image }),
      /* @__PURE__ */ React.createElement(CardContent, __spreadProps$1(__spreadValues$2({}, content), { disabled, t }))
    ))
  );
};
Card.propTypes = {
  imageSrc: PropTypes.string,
  content: PropTypes.object,
  direction: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  imageWidth: PropTypes.string,
  imageHeight: PropTypes.string,
  disabled: PropTypes.boolean,
  t: PropTypes.func.isRequired
};

const ROOT = "vertical-embed";

const convertDuration = (durationInMinutes, t) => {
  if (!isNumber(durationInMinutes))
    return "";
  if (durationInMinutes < 60) {
    return t("VerticalEmbed_Units_Minute", { minutes: durationInMinutes });
  }
  const hours = durationInMinutes / 60;
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  const durationInHours = rminutes === 0 ? t("VerticalEmbed_Units_Hour", { hours: rhours }) : t("VerticalEmbed_Units_Hour", { hours: rhours }) + " " + t("VerticalEmbed_Units_Minute", { minutes: rminutes });
  return durationInHours;
};

const getBookingContent = ({ name, bookingData: { durations } }, t) => ({
  title: name,
  info: { leftSubtitle: durations && convertDuration(durations, t) },
  buttonText: t("VerticalEmbed_Bookings_Button")
});
const getEventContent = ({ name, eventData: { scheduling, location } }, t) => ({
  title: name,
  info: { leftSubtitle: scheduling, rightSubtitle: location },
  buttonText: t("VerticalEmbed_Events_Button")
});
const getProductContent = ({ name }, t) => ({
  title: name,
  buttonText: t("VerticalEmbed_Products_Button")
});
const contentTypeMapGetter = {
  BOOKING: getBookingContent,
  EVENT: getEventContent,
  PRODUCT: getProductContent
};
const verticalsDataBuilder = (componentData, t, changeBaseUrl = (x) => x) => {
  const { url, image } = componentData;
  return {
    url: changeBaseUrl(url),
    imageSrc: image.src.url,
    imageWidth: image.width,
    imageHeight: image.height,
    content: contentTypeMapGetter[componentData.type](componentData, t)
  };
};

var __defProp$1 = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const VerticalEmbedViewer = ({ settings, componentData, helpers }) => {
  const { t, locale } = useContext(RicosContext);
  const onClick = () => {
    var _a;
    return (_a = helpers.onViewerAction) == null ? void 0 : _a.call(helpers, VERTICAL_EMBED_TYPE, "Click", componentData.type);
  };
  const props = verticalsDataBuilder(componentData, t, settings.changeBaseUrl);
  const direction = getLangDir(locale);
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classNames(styles.card, { [styles.slimLayout]: settings.slimLayout }),
        "data-hook": ROOT,
        onClick
      },
      /* @__PURE__ */ React.createElement(Card, __spreadProps(__spreadValues$1({}, props), { t, direction, disabled: settings.disabled }))
    )
  );
};

const VerticalEmbed = ({ node, nodeIndex, NodeViewContainer }) => {
  const { helpers, config } = useContext(RicosViewerContext);
  return /* @__PURE__ */ React.createElement(NodeViewContainer, { node, nodeIndex }, /* @__PURE__ */ React.createElement(
    VerticalEmbedViewer,
    {
      settings: config[VERTICAL_EMBED_TYPE] || {},
      componentData: node.appEmbedData,
      helpers
    }
  ));
};

const nodeViewRenderers = {
  [Node_Type.APP_EMBED]: VerticalEmbed
};

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const pluginVerticalEmbed = (config) => {
  return {
    config: __spreadValues({}, config),
    type: VERTICAL_EMBED_TYPE,
    nodeViewRenderers
  };
};

export { VERTICAL_EMBED_TYPE, pluginVerticalEmbed };
//# sourceMappingURL=viewer.js.map
